import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import { radius } from '@karehero/llama/theme/radius';

import ChangelogTable from '@/designs-dev/inspect/ChangelogTable';
import {
  APPROACH_GUIDANCE,
  APPROACH_META,
  type DsEntry,
} from '@/designs-dev/inspect/ds-registry';

// One design-system registry entry as a readable card: approach, theme
// change, tokens, changelog, rationale, file. Shared by the hand-over sheet
// and the feature handover pages.
const EntryCard = ({ name, entry }: { name: string; entry: DsEntry }) => (
  <Card sx={{ p: 3 }}>
    <Stack spacing={2}>
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
        direction='row'
      >
        <Typography variant='h4' color='primary.main'>
          {name}
        </Typography>
        <Chip
          size='small'
          label={APPROACH_META[entry.approach].label}
          color={APPROACH_META[entry.approach].color as never}
        />
      </Stack>

      {/* what the approach means for whoever builds it */}
      <Typography variant='body2'>
        {APPROACH_GUIDANCE[entry.approach]}
      </Typography>

      {entry.themeChange && (
        <Box
          sx={{
            border: 1,
            borderColor:
              entry.themeChange.status === 'proposed'
                ? 'custom.warmOrange.500'
                : 'custom.neutral.200',
            borderRadius: `${radius.md}px`,
            p: 1.5,
            bgcolor:
              entry.themeChange.status === 'proposed'
                ? 'custom.warmOrange.50'
                : 'custom.neutral.50',
          }}
        >
          <Typography variant='subtitle2'>
            {entry.themeChange.status === 'proposed'
              ? 'Proposed theme change'
              : 'Shipped theme change (on prod)'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {entry.themeChange.note}
          </Typography>
        </Box>
      )}

      {entry.tokens.length > 0 && (
        <Box>
          <Typography variant='overline' color='text.secondary'>
            How it’s built
          </Typography>
          <Stack sx={{ m: 0, pl: 2.5 }} component='ul' spacing={0.25}>
            {entry.tokens.map((t, i) => (
              <Typography
                key={i}
                component='li'
                variant='body2'
                color='text.secondary'
              >
                {t}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {entry.changelog && entry.changelog.length > 0 && (
        <Box>
          <Typography variant='overline' color='text.secondary'>
            Changelog
          </Typography>
          <ChangelogTable rows={entry.changelog} />
        </Box>
      )}

      {entry.rationale && (
        <Typography variant='body2' color='text.secondary'>
          {entry.rationale}
        </Typography>
      )}
      {entry.file && (
        <Typography variant='mono' color='text.secondary'>
          {entry.file}
        </Typography>
      )}
    </Stack>
  </Card>
);

export default EntryCard;
